import React, { useState } from "react";
import { useTheme } from '../../context/ThemeContext';
import axios from "axios";
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faInfoCircle,
  faEnvelope,
  faServer,
  faShieldAlt,
  faPercentage,
  faUser,
  faChevronUp,
  faChevronDown
} from "@fortawesome/free-solid-svg-icons";

const EmailChecker = () => {
  const [email, setEmail] = useState("");
   useTheme();
     const [validationResult, setValidationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showDetails, setShowDetails] = useState(false);

  const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const checkEmail = async () => {
    setValidationResult(null);

    if (!email) {
      setValidationResult({
        status: "error",
        message: "Please enter an email address",
        icon: faTimesCircle
      });
      return;
    }

    if (!isValidEmail(email)) {
      setValidationResult({
        status: "error",
        message: "Invalid email format",
        icon: faTimesCircle
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        'https://backend.onlinetoolpot.com/api/validate',
        { email },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = response.data;
      console.log("Email validation response:", response);  
      console.log("Email validation data:", data);

      // Add to history
      const newEntry = {
        email,
        timestamp: new Date().toLocaleString(),
        valid: data.success && data.data.final_conclusion === "likely_valid"
      };
      setHistory([newEntry, ...history.slice(0, 4)]);

      // Set validation result
      setValidationResult(data.data);
    } catch (error) {
      console.error("Error validating email:", error);
      setValidationResult({
        status: "error",
        message: "Error checking the email. Try again.",
        icon: faTimesCircle
      });
    }

    setLoading(false);
  };

  const getStatusIcon = () => {
    if (!validationResult) return null;
    
    if (validationResult.status === "error") {
      return <FontAwesomeIcon icon={faTimesCircle} className="text-red-500" />;
    }

    if (validationResult.final_conclusion === "likely_valid") {
      return <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />;
    } else if (validationResult.steps.privacy_shield.is_disposable) {
      return <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500" />;
    } else {
      return <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500" />;
    }
  };

  const getStatusMessage = () => {
    if (!validationResult) return null;

    if (validationResult.status === "error") {
      return validationResult.message;
    }

    if (validationResult.final_conclusion === "likely_valid") {
      return "This is a valid and deliverable email address";
    } else if (validationResult.steps.privacy_shield.is_disposable) {
      return "This is a disposable/temporary email address";
    } else {
      return "Could not fully verify this email";
    }
  };

  const getStatusClass = () => {
    if (!validationResult) return "";

    if (validationResult.status === "error") {
      return "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
    }

    if (validationResult.final_conclusion === "likely_valid") {
      return "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
    } else if (validationResult.steps.privacy_shield.is_disposable) {
      return "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700";
    } else {
      return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700";
    }
  };

  return (
    <>
    <HeaderSection toolId="emailValidator" />
    <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
          <h1 className="text-2xl font-bold mb-2 flex items-center">
            <FontAwesomeIcon icon={faEnvelope} className="mr-3" />
            Advanced Email Validator
          </h1>
          <p className="opacity-90">
            Verify email addresses in real-time with comprehensive validation checks
          </p>
        </div>

        <div className="p-6">
          {/* Input Section */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Email Address
                </label>
                <input
                  id="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email to validate"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-700 dark:bg-gray-700 dark:text-white dark:border-gray-600"
                  aria-label="Email address to validate"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={checkEmail}
                  disabled={loading}
                  className={`px-6 py-3 rounded-lg font-medium ${
                    loading ? "bg-gray-300" : "bg-green-500 hover:bg-green-600"
                  } text-white transition-colors flex items-center`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Validating...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                      Validate Email
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Results Section */}
          {validationResult && (
            <div className="mb-8">
              <div className={`p-4 rounded-lg border ${getStatusClass()} mb-4`}>
                <div className="flex items-center">
                  {getStatusIcon()}
                  <span className="ml-2 font-medium">{getStatusMessage()}</span>
                </div>
              </div>

              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 text-sm font-medium flex items-center mb-4"
              >
                {showDetails ? "Hide details" : "Show detailed analysis"}
                <FontAwesomeIcon
                  icon={showDetails ? faChevronUp : faChevronDown}
                  className="ml-1 text-xs"
                />
              </button>

              {showDetails && validationResult.email && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-700 dark:text-white mb-3 flex items-center">
                      <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-green-500" />
                      Email Information
                    </h3>
                    <ul className="space-y-2">
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Email Address:</span>
                        <span className="font-medium text-blue-500">{validationResult.email}</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Domain:</span>
                        <span className="font-medium text-blue-500">{validationResult.steps.privacy_shield.domain}</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Format Valid:</span>
                        <span className={validationResult.steps.syntax_check.valid ? "text-green-500 font-medium" : "text-red-500 font-medium"}>
                          {validationResult.steps.syntax_check.valid ? "Yes" : "No"}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-700 dark:text-white mb-3 flex items-center">
                      <FontAwesomeIcon icon={faServer} className="mr-2 text-green-500" />
                      Server Checks
                    </h3>
                    <ul className="space-y-2">
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">MX Records Found:</span>
                        <span className={validationResult.steps.mx_check.valid ? "text-green-500 font-medium" : "text-red-500 font-medium"}>
                          {validationResult.steps.mx_check.valid ? "Yes" : "No"}
                        </span>
                      </li>
                      <li className="flex flex-col">
                        <span className="text-gray-600 dark:text-gray-300 mb-1">MX Hosts:</span>
                        <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 p-2 rounded">
                          {validationResult.steps.mx_check.hosts.join(", ")}
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-700 dark:text-white mb-3 flex items-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="mr-2 text-green-500" />
                      Security Checks
                    </h3>
                    <ul className="space-y-2">
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Disposable Email:</span>
                        <span className={validationResult.steps.privacy_shield.is_disposable ? "text-yellow-500 font-medium" : "text-green-500 font-medium"}>
                          {validationResult.steps.privacy_shield.is_disposable ? "Yes" : "No"}
                        </span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Privacy Shield:</span>
                        <span className={validationResult.steps.privacy_shield.is_shielded ? "text-green-500 font-medium" : "text-gray-500 dark:text-gray-400 font-medium"}>
                          {validationResult.steps.privacy_shield.is_shielded ? "Yes" : "No"}
                        </span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Blocked Domain:</span>
                        <span className={validationResult.steps.privacy_shield.is_blocked ? "text-red-500 font-medium" : "text-green-500 font-medium"}>
                          {validationResult.steps.privacy_shield.is_blocked ? "Yes" : "No"}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h3 className="font-medium text-gray-700 dark:text-white mb-3 flex items-center">
                      <FontAwesomeIcon icon={faUser} className="mr-2 text-green-500" />
                      Username Analysis
                    </h3>
                    <ul className="space-y-2">
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Username:</span>
                        <span className="font-medium text-blue-500">{validationResult.steps.username_analysis.username}</span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Role Account:</span>
                        <span className={validationResult.steps.username_analysis.is_role_account ? "text-yellow-500 font-medium" : "text-green-500 font-medium"}>
                          {validationResult.steps.username_analysis.is_role_account ? "Yes" : "No"}
                        </span>
                      </li>
                      <li className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Spam Risk:</span>
                        <span className="font-medium capitalize">
                          {validationResult.steps.username_analysis.spam_risk === "low" ? (
                            <span className="text-green-500">Low</span>
                          ) : validationResult.steps.username_analysis.spam_risk === "medium" ? (
                            <span className="text-yellow-500">Medium</span>
                          ) : (
                            <span className="text-red-500">High</span>
                          )}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg col-span-1 md:col-span-2">
                    <h3 className="font-medium text-gray-700 dark:text-white mb-3 flex items-center">
                      <FontAwesomeIcon icon={faPercentage} className="mr-2 text-green-500" />
                      Risk Assessment
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-600 dark:text-gray-300">Risk Score:</span>
                          <span className="font-medium">
                            {validationResult.risk_score < 30 ? (
                              <span className="text-green-500">{validationResult.risk_score}/100 (Low)</span>
                            ) : validationResult.risk_score < 70 ? (
                              <span className="text-yellow-500">{validationResult.risk_score}/100 (Medium)</span>
                            ) : (
                              <span className="text-red-500">{validationResult.risk_score}/100 (High)</span>
                            )}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-600">
                          <div
                            className={`h-2.5 rounded-full ${
                              validationResult.risk_score < 30 ? "bg-green-500" :
                              validationResult.risk_score < 70 ? "bg-yellow-500" : "bg-red-500"
                            }`}
                            style={{ width: `${validationResult.risk_score}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Confidence:</span>
                        <span className="font-medium text-blue-500">{validationResult.confidence}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-300">Final Conclusion:</span>
                        <span className="font-medium capitalize">
                          {validationResult.final_conclusion.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History Section */}
          {history.length > 0 && (
            <div className="border-t pt-6 mt-6 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">Recent Validations</h3>
              <div className="space-y-2">
                {history.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      item.valid ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300" : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    }`}
                  >
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={item.valid ? faCheckCircle : faTimesCircle}
                        className={`mr-2 ${item.valid ? "text-green-500" : "text-red-500"}`}
                      />
                      <span>{item.email}</span>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{item.timestamp}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    <BottomSection toolId="emailValidator" />
    </>
  );
};

export default EmailChecker;